import {
  ArrowRight,
  BookOpen,
  Building2,
  ExternalLink,
  Gauge,
  KeyRound,
  Plus,
  ReceiptText,
  ShieldCheck,
  Store,
  UserPlus,
} from 'lucide-react';
import { useMemo, type ComponentType, type SVGProps } from 'react';
import { Link } from 'react-router-dom';

import {
  usePortalGetPortalMeClient,
  usePortalGetPortalMerchants,
  usePortalGetPortalTransactions,
} from '@/lib/api/portal/portal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CountUp } from '@/components/CountUp';
import { useHasClientRole, usePortalAuth } from '@/lib/portal-auth/usePortalAuth';
import { cn } from '@/lib/utils';

type IconKind = ComponentType<SVGProps<SVGSVGElement>>;

export default function PortalDashboardPage() {
  const { user } = usePortalAuth();
  const canManage = useHasClientRole('ClientAdmin');

  const meClient = usePortalGetPortalMeClient();
  const merchants = usePortalGetPortalMerchants();
  const txn = usePortalGetPortalTransactions({ limit: 5 });

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    if (h < 21) return 'Good evening';
    return 'Welcome back';
  }, []);

  const firstName = (user?.fullName || user?.email || 'there').split(/\s|[@.]/)[0];

  const merchantCount = merchants.data?.length ?? 0;
  const storeCount = useMemo(
    () => merchants.data?.reduce((sum, m) => sum + Number(m.storeCount ?? 0), 0) ?? 0,
    [merchants.data],
  );
  const totalTxn = Number(txn.data?.total ?? 0);

  return (
    <section className="space-y-6">
      {/* ---- hero ---- */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{greeting},</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight">{firstName}.</h1>
          {meClient.data && (
            <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
              <span>Signed in to</span>
              <span className="font-medium text-foreground">{meClient.data.name}</span>
              <span className="inline-flex items-center rounded-full border border-border/60 bg-background/60 px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
                {meClient.data.clientId}
              </span>
            </p>
          )}
        </div>
        <Button asChild variant="outline" className="gap-2">
          <Link to="/portal/transactions">
            View all transactions
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </header>

      {/* ---- KPI grid ---- */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          icon={Building2}
          iconTone="primary"
          label="Merchants"
          sub="your Adyen merchant accounts"
          numericValue={merchants.isLoading ? null : merchantCount}
          format={(n) => String(Math.round(n))}
          cta={{ to: '/portal/merchants', label: 'Manage' }}
        />
        <KpiCard
          icon={Store}
          iconTone="emerald"
          label="Stores"
          sub="across all merchants"
          numericValue={merchants.isLoading ? null : storeCount}
          format={(n) => String(Math.round(n))}
        />
        <KpiCard
          icon={ReceiptText}
          iconTone="amber"
          label="Transactions"
          sub="lifetime"
          numericValue={txn.isLoading ? null : totalTxn}
          format={(n) => formatNumber(n)}
          cta={{ to: '/portal/transactions', label: 'Review' }}
        />
        <KpiCard
          icon={Gauge}
          iconTone="primary"
          label="Rate limit"
          sub="requests / minute"
          numericValue={
            meClient.isLoading || meClient.data?.rateLimitPerMin == null
              ? null
              : Number(meClient.data.rateLimitPerMin)
          }
          format={(n) => String(Math.round(n))}
        />
      </div>

      {/* ---- API documentation ---- */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div className="min-w-0">
            <CardTitle className="text-base">API documentation</CardTitle>
            <CardDescription>
              Interactive reference for every <code className="font-mono text-xs">/proxy/*</code>{' '}
              route — request/response shapes, authentication, error codes, and copy-paste cURL
              snippets. Open in a new tab.
            </CardDescription>
          </div>
          <Button asChild variant="outline" className="shrink-0 gap-2">
            <a
              href={`${import.meta.env.VITE_API_TARGET ?? window.location.origin}/scalar/partner`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <BookOpen className="h-4 w-4" />
              Open API docs
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </Button>
        </CardHeader>
      </Card>

      {/* ---- secondary row ---- */}
      <div className="grid gap-4 lg:grid-cols-5">
        {/* recent transactions */}
        <Card className="lg:col-span-3">
          <CardHeader className="flex flex-row items-start justify-between space-y-0">
            <div>
              <CardTitle className="text-base">Recent calls</CardTitle>
              <CardDescription>Your last 5 proxy requests.</CardDescription>
            </div>
            <Link
              to="/portal/transactions"
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-accent/50"
            >
              See all
              <ArrowRight className="h-3 w-3" />
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {txn.isLoading ? (
              <div className="space-y-2 p-6">
                {Array.from({ length: 4 }, (_, i) => (
                  <div key={i} className="h-10 animate-pulse rounded-md bg-muted/40" />
                ))}
              </div>
            ) : !txn.data || txn.data.items.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-6 py-10 text-center text-sm text-muted-foreground">
                <ReceiptText className="h-6 w-6 opacity-50" />
                <span>No calls yet — fire one from your partner integration to see it here.</span>
              </div>
            ) : (
              <ul className="divide-y divide-border/60 text-sm">
                {txn.data.items.map((t) => (
                  <li key={t.id} className="flex items-center gap-3 px-6 py-3">
                    <StatusDot code={Number(t.statusCode)} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="truncate font-mono text-xs font-medium text-foreground">
                          {t.method} {t.endpoint}
                        </span>
                        <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">
                          {Number(t.durationMs)} ms
                        </span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                        <span>{formatRelative(t.createdAt)}</span>
                        {t.merchantId && (
                          <Badge variant="outline" className="h-4 px-1 font-mono text-[10px]">
                            {t.merchantId}
                          </Badge>
                        )}
                        {t.storeId && (
                          <Badge variant="outline" className="h-4 px-1 font-mono text-[10px]">
                            {t.storeId}
                          </Badge>
                        )}
                        {t.errorCode && (
                          <Badge variant="destructive" className="h-4 px-1 text-[10px]">
                            {t.errorCode}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
                      {t.statusCode}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* quick actions + status */}
        <div className="space-y-4 lg:col-span-2">
          {canManage && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Quick actions</CardTitle>
                <CardDescription>Spin up your workspace faster.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button asChild variant="outline" className="w-full justify-start gap-2">
                  <Link to="/portal/merchants">
                    <Plus className="h-4 w-4" />
                    Add merchant
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full justify-start gap-2">
                  <Link to="/portal/users">
                    <UserPlus className="h-4 w-4" />
                    Invite a portal user
                  </Link>
                </Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Integration health</CardTitle>
              <CardDescription>At-a-glance status of your setup.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <StatusRow
                icon={Building2}
                label="Merchants configured"
                ok={merchantCount > 0}
                okText={`${merchantCount}`}
                warnText="None yet"
              />
              <StatusRow
                icon={Store}
                label="Stores configured"
                ok={storeCount > 0}
                okText={`${storeCount}`}
                warnText="None yet"
              />
              <StatusRow
                icon={ShieldCheck}
                label="Client active"
                ok={!!meClient.data?.isActive}
                okText="Active"
                warnText="Suspended"
              />
              <StatusRow
                icon={KeyRound}
                label="Rate limit headroom"
                ok={Number(meClient.data?.rateLimitPerMin ?? 0) > 0}
                okText={`${meClient.data?.rateLimitPerMin ?? 0} / min`}
                warnText="Not set"
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// KPI card
// ---------------------------------------------------------------------------

interface KpiCardProps {
  icon: IconKind;
  iconTone: 'primary' | 'emerald' | 'amber' | 'red';
  label: string;
  sub: string;
  numericValue: number | null;
  format: (n: number) => string;
  cta?: { to: string; label: string };
}

function KpiCard({ icon: Icon, iconTone, label, sub, numericValue, format, cta }: KpiCardProps) {
  const tone = {
    primary: 'bg-primary/10 text-primary',
    emerald: 'bg-emerald-500/10 text-emerald-500',
    amber: 'bg-amber-500/10 text-amber-500',
    red: 'bg-rose-500/10 text-rose-500',
  }[iconTone];

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div>
          <CardDescription className="text-xs font-medium uppercase tracking-wider">
            {label}
          </CardDescription>
          <div className="mt-3 text-3xl font-semibold tabular-nums tracking-tight">
            <CountUp value={numericValue} format={format} />
          </div>
        </div>
        <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', tone)}>
          <Icon className="h-5 w-5" />
        </span>
      </CardHeader>
      <CardContent className="flex items-center justify-between pt-0">
        <p className="text-xs text-muted-foreground">{sub}</p>
        {cta && (
          <Link
            to={cta.to}
            className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium text-primary hover:bg-accent/50"
          >
            {cta.label}
            <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Row bits
// ---------------------------------------------------------------------------

function StatusDot({ code }: { code: number }) {
  const tone =
    code >= 200 && code < 300
      ? 'bg-emerald-400'
      : code >= 400 && code < 500
        ? 'bg-amber-400'
        : code >= 500 || code === 0
          ? 'bg-rose-500'
          : 'bg-muted-foreground';
  return <span className={cn('h-2 w-2 shrink-0 rounded-full', tone)} aria-hidden />;
}

interface StatusRowProps {
  icon: IconKind;
  label: string;
  ok: boolean;
  okText: string;
  warnText: string;
}

function StatusRow({ icon: Icon, label, ok, okText, warnText }: StatusRowProps) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-card/40 px-3 py-2 text-sm">
      <div className="flex min-w-0 items-center gap-2">
        <Icon
          className={cn(
            'h-4 w-4 shrink-0',
            ok ? 'text-emerald-500' : 'text-amber-500',
          )}
        />
        <span className="truncate text-muted-foreground">{label}</span>
      </div>
      <span
        className={cn(
          'shrink-0 text-xs font-semibold tabular-nums',
          ok ? 'text-foreground' : 'text-amber-500',
        )}
      >
        {ok ? okText : warnText}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(Math.round(value));
}

function formatRelative(iso: string): string {
  const delta = (Date.now() - new Date(iso).getTime()) / 1000;
  if (delta < 60) return `${Math.round(delta)}s ago`;
  if (delta < 3600) return `${Math.round(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.round(delta / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}
